import type { ColumnDef } from "@tanstack/react-table";
import { EmailReactTable } from "components/EmailReactTable";
import { RelativeTimeDisplay } from "components/RelativeTimeDisplay/RelativeTimeDisplay";
import { Badge } from "components/ui/Badge";
import { Button } from "components/ui/Button";
import { HYPHEN } from "constants/search-table";
import type { FC } from "react";
import { useCallback } from "react";
import type {
  EmailWithStatus,
  SelectContextMenuHandler,
} from "routes/emails/SearchEmailsPage";
import { FreeWordHighlighter } from "routes/emails/SearchEmailsPage/components/FreeWordHighlighter";
import { searchGmail } from "utils/gmail-utils";
import { formatToManYen, omitText } from "utils/text-utils";
import { ReactComponent as GmailIcon } from "../../../../../components/EmailCard/gmail.svg";
import { ReactComponent as StarIcon } from "../star.svg";

export type HREmailsTableUIProps = {
  emails: EmailWithStatus[];
  onSelectEmail: (id: string) => void;
  onToggleFavoriteEmail: (emailId: string, favorite: boolean) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  freeWords: string[];
};

export type HRRow = {
  emailId: string;
  read: boolean;
  star: JSX.Element;
  from: JSX.Element;
  dateTime: JSX.Element;
  duplicateCount: JSX.Element;
  age: string;
  gender: string;
  station: JSX.Element;
  hrTypes: string;
  startedDate: JSX.Element;
  reward: string;
  isRemoteNeeded: JSX.Element;
  menu: JSX.Element;
};

export const HREmailsTableUI: FC<HREmailsTableUIProps> = ({
  emails,
  onSelectEmail,
  onSelectContextMenu,
  onToggleFavoriteEmail,
  freeWords,
}) => {
  const columns: ColumnDef<HRRow>[] = [
    {
      accessorKey: "emailId",
      header: "emailId",
    },
    {
      accessorKey: "star",
      header: "☆",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "from",
      header: "From",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "dateTime",
      header: "日付",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "duplicateCount",
      header: "重複",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "age",
      header: "年齢",
    },
    {
      accessorKey: "gender",
      header: "性別",
    },
    {
      accessorKey: "station",
      header: "最寄駅",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "hrTypes",
      header: "人材種別",
    },
    {
      accessorKey: "startedDate",
      header: "稼働時期",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "reward",
      header: "希望金額",
    },
    {
      accessorKey: "isRemoteNeeded",
      header: "リモート希望",
      cell: props => props.getValue(),
    },
    {
      accessorKey: "menu",
      header: "",
      cell: props => props.getValue(),
    },
  ];

  const genderText = useCallback((gender: string | null) => {
    if (!gender) {
      return HYPHEN;
    }
    if (gender === "male") {
      return "男性";
    }
    if (gender === "female") {
      return "女性";
    }
    return HYPHEN;
  }, []);

  const ageText = useCallback((age: string | null) => {
    if (!age) {
      return HYPHEN;
    }
    if (Number.isNaN(Number(age))) {
      return HYPHEN;
    }
    return `${age}歳`;
  }, []);

  const hrTypesText = useCallback((hrType: string | null) => {
    switch (hrType) {
      case "fullTimeEmployee":
        return "正社員";
      case "contractEmployee":
        return "契約社員";
      case "freelancer":
        return "フリーランス";
      default:
        return HYPHEN;
    }
  }, []);

  const data: HRRow[] = emails.map((email: EmailWithStatus) => ({
    emailId: email.id,
    read: email.read,
    // お気に入りボタン
    star: (
      <StarIcon
        className={`${
          email.favorite ? "text-yellow-300" : "text-gray-200"
        } w-5 h-5`}
        onClick={e => {
          onToggleFavoriteEmail(email.id, !email.favorite);
          e.stopPropagation();
        }}
      />
    ),
    // 差し出し元 (名前のみ表示、メールアドレスはホバー時 tooltip)
    from: (
      <div
        className="tooltip tooltip-right relative"
        data-tip={email.from.address}
      >
        {!email.read && (
          <div className="absolute -left-4 top-1">
            <div className="badge badge-xs badge-info opacity-80 scale-75 animate-[pulse_3s_ease-in-out_infinite]" />
          </div>
        )}
        <span className="text-xs">{omitText(email.from.name, 16)}</span>
      </div>
    ),
    // 日付
    dateTime: <RelativeTimeDisplay date={email.date} />,
    // 重複
    duplicateCount:
      email.duplicateCount != null && email.duplicateCount >= 2 ? (
        <Badge color="secondary" isOutline size="xs">
          {email.duplicateCount}件
        </Badge>
      ) : (
        <>{HYPHEN}</>
      ),
    // 年齢
    age: ageText(email?.hrColumn?.age || null),
    // 性別
    gender: genderText(email?.hrColumn?.gender || null),
    // 現場駅
    station: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(email?.hrColumn?.station)}
      />
    ),
    // 人材種別
    hrTypes: hrTypesText(email?.hrColumn?.hrTypes || null),
    // 稼働日
    startedDate: (
      <FreeWordHighlighter
        type="Table"
        freeWords={freeWords}
        textToHighlight={omitText(email?.hrColumn?.startedDate)}
      />
    ),
    // 希望金額
    reward: formatToManYen(email?.hrColumn?.reward),
    // リモート
    isRemoteNeeded: email?.hrColumn?.isRemoteNeeded ? (
      <Badge color="success" isOutline size="xs">
        ◯
      </Badge>
    ) : (
      <Badge color="error" isOutline size="xs">
        ✕
      </Badge>
    ),
    // メニュー
    menu: (
      <div className="dropdown dropdown-end">
        <Button
          tabIndex={0}
          variant="ghost"
          size="xs"
          onClick={e => e.stopPropagation()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            className="h-4 w-4 stroke-current"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
            />
          </svg>
        </Button>
        <ul
          className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52"
          onClick={e => e.stopPropagation()}
        >
          {/* Gmail検索ボタン */}
          <li>
            <a
              onClick={e => {
                e.stopPropagation();
                searchGmail(email.subject, email.originalMessageId);
              }}
            >
              <GmailIcon className="w-4 h-4 inline-block mr-2" />
              Gmailで表示
            </a>
          </li>
        </ul>
      </div>
    ),
  }));
  return data.length === 0 ? (
    <div className="flex justify-center">
      <div className="text-left text-gray-500 max-w-xl">
        <p>条件に合う検索結果はありません。</p>
        <div className="h-4" />
        <p>あるいは、あなたのアカウントがどの会社にも紐づいていません。</p>
        <p>管理者にアカウントと所属会社の紐づけ作業を依頼してください。</p>
      </div>
    </div>
  ) : (
    <EmailReactTable
      data={data}
      columns={columns}
      onSelectEmail={onSelectEmail}
      onSelectContextMenu={onSelectContextMenu}
    />
  );
};
